/**
 * Author: Claude Code
 * Generation: Agent creation page with 3-step wizard
 * Version: v3.2.141
 * Modified: 2025-10-17 (Fixed game navigation: /game → /game-page for complete UI)
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRoomStore } from '@store/RoomStore';
import { useAuthStore } from '@store/AuthStore';
import Step1Appearance from '../components/agent-creation/Step1Appearance';
import Step2Attributes from '../components/agent-creation/Step2Attributes';
import Step3Strategy from '../components/agent-creation/Step3Strategy';
// GlobalLoadingOverlay is now mounted in main.tsx; no local LoadingScreen needed

export default function AgentCreationPage() {
  const navigate = useNavigate();
  // Global overlay handles progress; local flag removed

  const {
    agentCreationStep,
    agentCreationTimeRemaining,
    setAgentCreationTime,
    nextAgentCreationStep,
    previousAgentCreationStep,
    myAgentConfiguration,
    getRemainingAttributePoints,
    getCompleteAgentData,
    resetAgentCreation,
    toggleWaitingRoom,
    submitAgentConfiguration,
    leaveRoom,
  } = useRoomStore();
  const { token } = useAuthStore();

  // Countdown timer - 5 minutes (300 seconds)
  useEffect(() => {
    setAgentCreationTime(300);
    const interval = setInterval(() => {
      setAgentCreationTime(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // ✅ CRITICAL FIX: Defensive guard - ensure config is initialized on page load
  useEffect(() => {
    if (!myAgentConfiguration) {
      console.warn('⚠️ [AgentCreationPage] myAgentConfiguration was null, force-initializing');
      resetAgentCreation();
    }
  }, [myAgentConfiguration, resetAgentCreation]);

  // Handle countdown expiration
  useEffect(() => {
    if (agentCreationTimeRemaining === 0) {
      // Check if agent configuration is complete
      const hasName = myAgentConfiguration?.name?.trim();
      const hasUsedAllPoints = getRemainingAttributePoints() === 0;
      const isOnFinalStep = agentCreationStep === 3;

      if (hasName && hasUsedAllPoints && isOnFinalStep) {
        console.warn('⏰ Time expired - Auto-submitting completed configuration');
        handleComplete();
      } else {
        console.warn('⏰ Time expired - Leaving room (incomplete configuration)');
        handleTimeoutLeave();
      }
    }
  }, [agentCreationTimeRemaining]);

  const handleTimeoutLeave = async () => {
    await leaveRoom();
    resetAgentCreation();
    navigate('/lobby');
  };

  const handleNext = () => {
    if (agentCreationStep === 1) {
      if (!myAgentConfiguration?.name?.trim()) {
        alert('Please enter an agent name');
        return;
      }
    }

    if (agentCreationStep === 2) {
      const remaining = getRemainingAttributePoints();
      if (remaining !== 0) {
        alert(`Please use all ${remaining} remaining points`);
        return;
      }
    }

    nextAgentCreationStep();
  };

  const handlePrevious = () => {
    if (agentCreationStep === 1) {
      navigate('/lobby');
    } else {
      previousAgentCreationStep();
    }
  };

  const handleComplete = async () => {
    const agentData = getCompleteAgentData();
    console.log('Agent Created:', agentData);

    // Solo mode 分支：直接调用 /api/solo/start，跳过 Waiting 页面
    const isSolo = (useRoomStore.getState().singlePlayerMode);
    if (isSolo) {
      try {
        if (!agentData) { alert('配置不完整'); return; }
        const selectedModelId = useRoomStore.getState().selectedModelId || 'doubao-seed-1-6-251015';
        // 仅使用 WebSocket 事件（生产环境稳定通道）
        const webSocketService = (await import('../services/websocket/WebSocketService')).default;
        const ack: any = await webSocketService.emit('solo:start', { agentConfig: agentData, modelId: selectedModelId });
        if (!ack?.success) {
          alert('Single-player start failed: ' + (ack?.error || 'UNKNOWN'));
          return;
        }
        resetAgentCreation();
        // 立即进入游戏页；不出现 Waiting 页面（携带本次 roomId，禁止使用任何本地回退）
        if (ack.roomId) {
          navigate(`/game/${ack.roomId}`);
        } else {
          alert('Single-player start failed: missing roomId in response');
          return;
        }
      } catch (e: any) {
        alert('Single-player start failed: ' + (e?.message || e));
      } finally {
        useRoomStore.getState().setSinglePlayerMode(false);
      }
      return;
    }

    // 常规房间分支：走现有流程
    const success = await submitAgentConfiguration();
    if (success) {
      toggleWaitingRoom(true);
      const webSocketService = (await import('../services/websocket/WebSocketService')).default;
      webSocketService.on('GAME_LOADING_STARTED', (data: any) => {
        console.log('🎮 Game loading started → navigate to /game-page:', data);
        toggleWaitingRoom(false);
        resetAgentCreation();
        navigate('/game-page');
      });
    } else {
      alert('Failed to submit agent configuration');
    }
  };

  const minutes = Math.floor(agentCreationTimeRemaining / 60);
  const seconds = agentCreationTimeRemaining % 60;

  // Handle LoadingScreen completion → navigate to game
  // No local LoadingScreen; navigation handled on GAME_LOADING_STARTED

  return (
    <div className="main-container">
      <div className="page active">
        <div className="content-card">
          <div className="card-header">
            <div className="creation-header">
              <h2 className="card-title">Create Your Agent</h2>
              <div className="timer-badge">
                <span>⏱️</span>
                <span>{minutes}:{seconds.toString().padStart(2, '0')}</span>
              </div>
            </div>

            <div className="stepper">
              {[1, 2, 3].map((step) => (
                <div
                  key={step}
                  className={`step ${agentCreationStep === step ? 'active' : ''} ${
                    agentCreationStep > step ? 'completed' : ''
                  }`}
                >
                  <div className="step-circle">{step}</div>
                  <div className="step-label">
                    {step === 1 ? 'Appearance' : step === 2 ? 'Attributes' : 'Strategy'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card-body">
            {agentCreationStep === 1 && <Step1Appearance />}
            {agentCreationStep === 2 && <Step2Attributes />}
            {agentCreationStep === 3 && <Step3Strategy />}

            {/* v3.2.49: Navigation buttons integrated into card-body */}
            <div className="step-nav-inline">
              <button className="btn btn-ghost btn-md" onClick={handlePrevious}>
                {agentCreationStep === 1 ? '← Lobby' : '← Back'}
              </button>
              {agentCreationStep < 3 ? (
                <button className="btn btn-primary btn-md" onClick={handleNext}>
                  Next →
                </button>
              ) : (
                <button className="btn btn-primary btn-md" onClick={handleComplete}>
                  Complete
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
